import React, { useState } from "react";
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { userService } from "../../_services";
import SignupHeader from "../Register/signup_header";
import Logo from "../../images/logo.png";
import Signupleft from "../../images/signupleft.webp";

const formSchema = z.object({
  email: z.string()
    .min(1, { message: "Please enter email" })
    .email({ message: "Invalid Email" }),
});

const ForgetPassword = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [resetLinkSent, setResetLinkSent] = useState(false);
    const [extraInfoMsg, setExtraInfoMsg] = useState('');
    const [showExtraInfo, setShowExtraInfo] = useState(false);

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: '',
        },
    });

    const onSubmit = async (values) => {
        setIsLoading(true);
        setShowExtraInfo(false);
        setExtraInfoMsg('');
        
        try {
            const apiData = await userService.pwdresetlink(values.email, "1");
            
            if (apiData.length > 0) {
                if (apiData[0].code !== "-1" && apiData[0].message !== "We've sent an account updation link. Please check your mail inbox/spam") {
                    setResetLinkSent(true);
                } else if (apiData[0].code === "-1" && apiData[0].message === "We've sent an account updation link. Please check your mail inbox/spam") {
                    setShowExtraInfo(true);
                    setExtraInfoMsg("This User is already in the system, Setup required. Setuplink mailed to you");
                } else {
                    form.setError('email', { message: apiData[0].message });
                    form.setValue('email', '');
                }
            }
        } catch (error) {
            form.setError('email', { message: 'An error occurred. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-secondary">
            <SignupHeader />
            
            <div className="w-full mx-auto">
                <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
                    <div className="w-full max-w-[1260px] mx-auto px-4 md:px-8">
                        <div className="flex flex-col md:flex-row items-stretch gap-8 md:gap-16">
                            {/* Left side - Image */}
                            <div className="flex-1 flex items-center justify-center md:max-w-[689px]">
                                <img 
                                    src={Signupleft} 
                                    alt="Reset your password" 
                                    className="w-full h-auto object-contain"
                                />
                            </div>

                            {/* Right side - Form */}
                            <div className="flex-1 flex items-center justify-center md:max-w-[490px]">
                                <div className="w-full space-y-6">
                                    {!resetLinkSent ? (
                                        <>
                                            <div className="space-y-2 text-center md:text-left">
                                                <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                                                    Reset Password
                                                </h1>
                                                <p className="text-md text-foreground opacity-70">
                                                    Enter your email address and we'll send you a link to reset your password
                                                </p>
                                            </div>

                                            <Form {...form}>
                                                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                                    <FormField
                                                        control={form.control}
                                                        name="email"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormControl>
                                                                    <div className="relative">
                                                                        <Input 
                                                                            {...field} 
                                                                            type="email"
                                                                            disabled={isLoading}
                                                                            placeholder=" "
                                                                            className="peer h-12"
                                                                            onChange={(e) => {
                                                                                field.onChange(e);
                                                                                setShowExtraInfo(false);
                                                                                setExtraInfoMsg('');
                                                                            }}
                                                                        />
                                                                        <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground">
                                                                            Email
                                                                        </FormLabel>
                                                                    </div>
                                                                </FormControl>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />

                                                    {showExtraInfo && extraInfoMsg && (
                                                        <Alert variant="info">
                                                            <AlertDescription>{extraInfoMsg}</AlertDescription>
                                                        </Alert>
                                                    )}

                                                    <Button type="submit" variant="secondary" size="lg" className="w-auto" disabled={isLoading}>
                                                        {isLoading ? (
                                                            <>
                                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                Sending...
                                                            </>
                                                        ) : (
                                                            'Send Reset Link'
                                                        )}
                                                    </Button>
                                                </form>
                                            </Form>

                                            <div className="mt-6 text-center md:text-left space-y-2">
                                                <p className="text-md text-foreground opacity-70">
                                                    Don't have an account?{' '}
                                                    <Link to="/signup" className="text-accent hover:no-underline hover:text-foreground font-medium">
                                                        Sign Up here
                                                    </Link>
                                                </p>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center space-y-6">
                                            <div className="flex justify-center">
                                                <div className="rounded-full bg-green-100 p-4">
                                                    <CheckCircle2 className="w-16 h-16 text-green-600" />
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <h2 className="text-3xl font-bold text-foreground">
                                                    Check Your Email
                                                </h2>
                                                <p className="text-md text-foreground opacity-70">
                                                    We've sent a password reset link to your email address.
                                                    Please check your inbox and follow the instructions.
                                                </p>
                                            </div>

                                            <div className="pt-4">
                                                <Link to="/login">
                                                    <Button variant="secondary" size="lg" className="w-auto">
                                                        Back to Sign In
                                                    </Button>
                                                </Link>
                                            </div>

                                            <p className="text-sm text-foreground opacity-70">
                                                Didn't receive the email? Check your spam folder or{' '}
                                                <Button 
                                                    type="button"
                                                    variant="link"
                                                    onClick={() => {
                                                        setResetLinkSent(false);
                                                        form.reset();
                                                    }}
                                                    className="p-0 h-auto text-accent hover:text-foreground font-medium"
                                                >
                                                    try again
                                                </Button>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgetPassword;
